import { HttpClient } from "@angular/common/http";
import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { filter, map, tap } from "rxjs/operators";
import { TableAbstractComponent } from "src/app/common-page-components/classes/table-abstract-component";
import { IHeaderConfig } from "src/app/common-page-components/headers-component/headers-component.component";
import {
  AthleteWCupPageStore,
  IWCupAthleteDetail,
} from "src/app/common-page-components/stores/entities-store/world-cup-athlete.store";
import { FilterWCupRankingService } from "src/app/world-ranking-statistics/wcup-ranking/services/filter-wcup-ranking.service";
import { AthleteWcupRankingFiltersStageService } from "../../services/athlete-wcup-ranking-filters-stage.service";
import { AthleteWcupRankingFiltersService } from "../../services/athlete-wcup-ranking-filters.service";

@Component({
  selector: "app-athlete-results-wcup-ranking-table",
  templateUrl: "./athlete-results-wcup-ranking-table.component.html",
  styleUrls: ["./athlete-results-wcup-ranking-table.component.scss"],
})
export class AthleteResultsWcupRankingTableComponent extends TableAbstractComponent {
  /**
   * The header to be presented before table.
   *
   * @type {string}
   * @memberof AthleteGeneralTableWcupRankingTableComponent
   */
  @Input() wCupName: string;

  /**
   * The process detail to be show as table rows.
   *
   * @type {Array<IWCupAthleteDetail>}
   * @memberof AthleteGeneralTableWcupRankingTableComponent
   */
  @Input() processDataDetail: Array<IWCupAthleteDetail> = [];

  /**
   * This data is complex, so we must perform some transformations.
   *
   * @type {Array<IWCupAthleteDetail>}
   * @memberof AthleteResultsWcupRankingTableComponent
   */
  processDataTransformation: Array<IWCupAthleteDetail> = [];

  /**
   * A flag to set the view with /Individual/Team/Mixed form.
   *
   * @type {boolean}
   * @memberof AthleteResultsWcupRankingTableComponent
   */
  isAll: boolean = false;

  /**
   * A property for select the hovered match
   *
   * @memberof AthleteCareerStatisticsComponent
   */
  isHoveredMatch = {};

  /*
   * The headers of this table
   *
   * @type {Array<IHeaderConfig>}
   * @memberof AthleteResultsWcupRankingTableComponent
   */
  headers: Array<IHeaderConfig> = [
    { name: "Year" }, // Year
    { name: "Stage 1" }, // Stage 1
    { name: "Stage 2" }, // Stage 2
    { name: "Stage 3" }, // Stage 3
    { name: "Stage 4" }, // Stage 4
    { name: "Final" }, // Final
  ];

  constructor(
    injector: Injector,
    public filter_service_copy: AthleteWcupRankingFiltersService,
  ) {
    super(
      injector,
      filter_service_copy,
      new AthleteWCupPageStore(injector.get(HttpClient), injector)
    );
  }

  /**
   * {@inheritdoc}
   *
   * @memberof AthleteGeneralTableWcupRankingTableComponent
   */
  ngOnInit() {
    super.ngOnInit();
    this.subscriptions.push(
      this.filter_service.orderValue
        .pipe(
          filter((value) => value.sortingType == null),
          tap((a) => this.changeSort("YEAR"))
        )
        .subscribe()
    );
    this.subscriptions.push(
      this.getFilterByEvent().subscribe((next) => {
        this.processDataTransformation = next;
      }),
      this.filter_service.orderValue
        .pipe(
          tap((a) => {
            const isAsc = a.asc ? 1 : -1;
            switch (a.sortingType as string) {
              case "YEAR":
                this.processDataDetail.sort((f, s) => {
                  return f.Year > s.Year ? -isAsc : isAsc;
                });
                break;
              case "STAGE":
                this.processDataDetail.sort((f, s) => {
                  return f.Place > s.Place ? -isAsc : isAsc;
                });
                break;
              case "EVENT":
                this.processDataDetail.sort((f, s) => {
                  return (f as any).Event > (s as any).Event ? -isAsc : isAsc;
                });
                break;
              case "RESULT":
                this.processDataDetail.sort((f, s) => {
                  return (f as any).Results["Rnk"] > (s as any).Results["Rnk"]
                    ? -isAsc
                    : isAsc;
                });
                break;
            }
          })
        )
        .subscribe()
    );

  }

  /**
   * A method to filter the data using the event combo. This performs and search and makes data deduplication.
   *
   * @return {*}
   * @memberof AthleteResultsWcupRankingTableComponent
   */
  getFilterByEvent() {
    return this.filter_service_copy.getDataStreamWithFilters().pipe(

      map((a) => {

        this.isAll = false;
        // We replace the event of mixed team avoid error on differents keys in frontend and API.
        const event = a[1].Name.replace('Mixed Team', 'Mixed');
        let category = a[0].Cat;
        if (event === 'Mixed') {
          category = category.replace(
            "M",
            "X"
          ).replace("W", "X")
        }
        const map = this.processDataDetail.map((object) => {
          Object.keys(object).forEach((key) => {
            let target;
            if (["Stage_1", "Stage_2", "Stage_3", "Stage_4", "Final"].indexOf(key) > -1) {

              target = object[key];
              const uniqueIds = [];
              object[key] = target.filter(f => f.Event === event);
              object[key] = object[key].filter(f => f.CatCode === category)
              object[key] = object[key].filter((element) => {
                const isDuplicate = uniqueIds.includes(element.Event);

                if (!isDuplicate) {
                  uniqueIds.push(element.Event);

                  return true;
                }

                return false;
              });

            }

          });
          return object;
        });
        return map;

      })
    );
  }

  /**
   * Method to set the hover match
   *
   * @param {*} e native event
   * @param {*} key value of stats row
   * @param {*} stage the stage number to allow every row to be simple.
   * @returns
   * @memberof AthleteResultsWcupRankingTableComponent
   */
  isHoverMatch(e, key, stage: string) {
    e.stopPropagation();
    this.isHoveredMatch = key.index.toString() + stage;
    return true;
  }

  /**
   * Method for unset the hover match
   *
   * @param {*} e native event
   * @param {*} key value of stats row
   * @returns
   * @memberof AthleteResultsWcupRankingTableComponent
   */
  isNotHoverMatch(e, key) {
    this.isHoveredMatch = undefined;
    return false;
  }

}
